import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@general/ui-kit'
import { realtime } from '@general/realtime/frontend'

/**
 * Notification feed plus the live stream.
 *
 * The stream is no longer this store's: it is `platform/realtime`, ONE
 * EventSource per tab for the whole ERP. Notifications is its first client and
 * only listens to its own events (`notification`) — plus `mail`, which it hands
 * on as a window event exactly as before. Everything about reconnecting (a
 * single retry engine, the ladder that fits the server's quota, coming back the
 * instant the network or the screen does) lives in that one place now; the test
 * that used to guard it here moved with it (platform/realtime/tests).
 *
 * `connect()` / `disconnect()` keep their names and meaning for the shell: the
 * first opens the shared stream, the second closes it on sign-out.
 */
export const useNotificationStore = defineStore('notifications', () => {
  const items = ref([])
  const unread = ref(0)
  const loading = ref(false)
  const connected = computed(() => realtime.connected.value)

  const latest = computed(() => items.value.slice(0, 8))

  // Subscribed once per store, not once per connect: the shared stream re-adds
  // its listeners on every reopen by itself.
  let subscribed = false
  function subscribe() {
    if (subscribed) return
    subscribed = true

    realtime.on('notification', (notification) => {
      if (!notification) return
      items.value.unshift(notification)
      unread.value += 1
    })

    // New mail for this person, read by the platform's mail client. Handed on
    // as a window event: one stream per tab, and the mail screen listens to it
    // without opening a second one.
    realtime.on('mail', (detail) => {
      try { window.dispatchEvent(new CustomEvent('general:mail', { detail })) } catch { /* ignore */ }
    })

    // A reopen after a gap: whatever arrived while it was down is in the
    // feed, not in the stream. Same rule as everywhere — the stream is the
    // shortcut, the database is the truth.
    realtime.onOpen(({ first }) => { if (!first) fetchFeed() })
  }

  async function fetchFeed({ limit = 50 } = {}) {
    loading.value = true
    try {
      const data = await api.get(`/notifications?limit=${limit}`)
      items.value = data.items
      unread.value = data.unread
    } catch {
      // A failed feed must not break the page it is embedded in.
    } finally {
      loading.value = false
    }
  }

  function connect() {
    subscribe()
    realtime.connect()
  }

  function disconnect() {
    realtime.disconnect()
  }

  async function markRead(id) {
    const notification = items.value.find(item => item.id === id)
    if (!notification || notification.read) return

    // Optimistic: the badge must react instantly, and the worst case is a
    // counter that corrects itself on the next fetch.
    notification.read = true
    unread.value = Math.max(0, unread.value - 1)
    try { await api.patch(`/notifications/${id}/read`) } catch { /* corrected on reload */ }
  }

  async function markAllRead() {
    items.value.forEach(item => { item.read = true })
    unread.value = 0
    try { await api.patch('/notifications/read-all') } catch { /* corrected on reload */ }
  }

  async function dismiss(id) {
    const index = items.value.findIndex(item => item.id === id)
    if (index === -1) return
    const [removed] = items.value.splice(index, 1)
    if (!removed.read) unread.value = Math.max(0, unread.value - 1)
    try { await api.delete(`/notifications/${id}`) } catch { /* corrected on reload */ }
  }

  function reset() {
    disconnect()
    items.value = []
    unread.value = 0
  }

  return { items, latest, unread, loading, connected, fetchFeed, connect, disconnect, markRead, markAllRead, dismiss, reset }
})
